/* eslint-disable indent */
import { EPostStatus } from '@constants';
import { IPost } from '@sdk/contracts';
import { dateProvider } from '@utils';
import { Button, Modal, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import {
  DateWrapper,
  FeaturedTag,
  HeadWrapper,
  InfoWrapper,
  PostWrapper,
  StyledImage,
  ImagePlaceholder,
  TitleWrapper,
  ActionsWrapper
} from './PostItem.styles';
import { formatAddresseesList } from './utils/formatAddresseesList';

import { Icon } from '@/components/Icon';
import { usePostViewer, usePostForm } from '@/hooks';
import { useProfile, useMutatePost } from '@/store';
import { formatScheduledDate } from '@/utils';

type Props = {
  post: IPost;
  isFeatured?: boolean;
  isEditable?: boolean;
  canOpenEditor?: boolean;
  hideDate?: boolean;
};

const ICON_SIZE = 16;
const ACTION_ICON_SIZE = 16;

export const PostItem: React.FC<Props> = ({
  post,
  isFeatured,
  isEditable = false,
  canOpenEditor,
  hideDate = false
}) => {
  const [modal, contextHolder] = Modal.useModal();
  const { t } = useTranslation('CommonDashboardTranslations');
  const { t: tDefault } = useTranslation('DefaultTranslations');
  const { setPostId } = usePostViewer();
  const { roleCheck } = useProfile();
  const { deletePost, publishNow } = useMutatePost();

  const { isManager, isAdmin, isTeacher } = roleCheck;
  const postData = post;

  const {
    _id,
    title,
    createdAt,
    featuredImage,
    tags = [],
    status,
    scheduledPublishDate,
    topic
  } = postData;

  const showDate = status !== EPostStatus.DRAFT && !hideDate;
  const displayTitle = title || t('NO_TITLE');
  const displayTopic = topic?.name || t('NO_TOPIC');
  const canEdit = isManager || isAdmin || isTeacher;
  const openEditorAllowed = canOpenEditor ?? isEditable;

  const { setPost, setIsOpen } = usePostForm();

  const { text: addresseeText, extraTags } = formatAddresseesList(tags, t);

  const isScheduled = canEdit && status === EPostStatus.SCHEDULED && scheduledPublishDate;

  const handleOpenPost = () => {
    if ((isScheduled || status === EPostStatus.DRAFT) && openEditorAllowed) {
      setPost(postData);
      setIsOpen(true);
    } else {
      setPostId(_id);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    modal.confirm({
      title: t('DELETE_POST_TITLE'),
      content: t('DELETE_POST_CONTENT'),
      okText: t('DELETE'),
      cancelText: tDefault('CANCEL'),
      okButtonProps: { danger: true },
      onOk: () => deletePost(_id)
    });
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPost(postData);
    setIsOpen(true);
  };

  const handlePublishNow = (e: React.MouseEvent) => {
    e.stopPropagation();
    modal.confirm({
      title: t('PUBLISH_NOW_TITLE'),
      content: t('PUBLISH_NOW_CONTENT'),
      okText: t('PUBLISH_NOW'),
      cancelText: tDefault('CANCEL'),
      onOk: () => {
        publishNow(_id);
      }
    });
  };

  return (
    <>
      {contextHolder}
      <PostWrapper onClick={handleOpenPost} $isFeatured={isFeatured}>
        {featuredImage?.url ? (
          <StyledImage src={featuredImage.url} alt="News preview" />
        ) : (
          <ImagePlaceholder>
            <Icon i="Image" size={24} color="black9" />
          </ImagePlaceholder>
        )}
        <InfoWrapper>
          {isFeatured && <FeaturedTag>{t('FEATURED_NEWS')}</FeaturedTag>}
          <HeadWrapper>
            {showDate && (
              <>
                <DateWrapper $isScheduled={!!isScheduled}>
                  {isScheduled && <Icon i="EarthEuropeAfricaFilled" size={ICON_SIZE} />}
                  <div>
                    {formatScheduledDate(
                      scheduledPublishDate
                        ? dateProvider.zonedTimeToUtc(scheduledPublishDate)
                        : new Date(createdAt),
                      tDefault
                    )}
                  </div>
                </DateWrapper>
                <span>·</span>
              </>
            )}

            <div>{displayTopic}</div>

            <span>·</span>
            <div title={extraTags?.join(' · ')}>{addresseeText}</div>
          </HeadWrapper>
          <TitleWrapper $isFeatured={isFeatured}>{displayTitle}</TitleWrapper>
        </InfoWrapper>
        {isEditable && (
          <ActionsWrapper className="post-actions">
            <Tooltip title={t('DELETE')}>
              <Button
                type="text"
                icon={<Icon i="TrashCan" size={ACTION_ICON_SIZE} />}
                onClick={handleDelete}
              />
            </Tooltip>
            {status !== EPostStatus.PUBLISHED && (
              <Tooltip title={t('EDIT')}>
                <Button
                  type="text"
                  icon={<Icon i="Edit" size={ACTION_ICON_SIZE} />}
                  onClick={handleEdit}
                />
              </Tooltip>
            )}
            {isScheduled && (
              <Tooltip title={t('PUBLISH_NOW')}>
                <Button
                  type="text"
                  icon={<Icon i="Send" size={ACTION_ICON_SIZE} />}
                  onClick={handlePublishNow}
                />
              </Tooltip>
            )}
          </ActionsWrapper>
        )}
      </PostWrapper>
    </>
  );
};
